/**
 * Менеджер типизированных элементов
 */

import { EventEmitter } from 'events';
import { TypedElement } from './TypedElement';
import {
  ITypedElementManager,
  TypedElementSchema,
  TypedFieldSchema,
  ITypedElement,
  SchemaValidationResult,
  TypeStats,
  TypedElementConfig,
  FieldType
} from '../types/typed-elements-interfaces.js';

/**
 * Менеджер для работы с типизированными элементами
 */
export class TypedElementManager extends EventEmitter implements ITypedElementManager {
  private schemas = new Map<string, TypedElementSchema>();
  private elements = new Map<string, ITypedElement>();
  private config: TypedElementConfig;
  private inheritanceGraph = new Map<string, string[]>(); // тип -> родители

  constructor(config?: Partial<TypedElementConfig>) {
    super();
    this.config = {
      strictMode: false,
      autoValidation: true,
      allowDynamicFields: true,
      inheritanceEnabled: true,
      maxInheritanceDepth: 10,
      cacheSchemas: true,
      defaultFieldValidation: {
        required: false,
        nullable: true
      },
      ...config
    };
  }

  // ===== Регистрация схем =====

  registerSchema<T>(schema: TypedElementSchema<T>): void {
    // Валидация схемы
    const validation = this.validateSchema(schema);
    if (!validation.isValid) {
      throw new Error(`Schema validation failed: ${validation.errors.join(', ')}`);
    }

    // Проверка наследования
    if (schema.inheritFrom) {
      if (!this.config.inheritanceEnabled) {
        throw new Error('Inheritance is disabled in configuration');
      }
      
      if (!this.schemas.has(schema.inheritFrom)) {
        throw new Error(`Parent schema ${schema.inheritFrom} not found`);
      }
      
      if (!this.validateInheritance(schema.name, schema.inheritFrom)) {
        throw new Error(`Invalid inheritance: ${schema.name} cannot inherit from ${schema.inheritFrom}`);
      }
    }

    this.schemas.set(schema.name, schema);
    this.updateInheritanceGraph(schema);
    
    this.emit('schema:registered', { schema });
  }

  unregisterSchema(typeName: string): void {
    if (!this.schemas.has(typeName)) {
      throw new Error(`Schema ${typeName} not found`);
    }

    // Проверяем, есть ли зависимые схемы
    const dependentSchemas = Array.from(this.schemas.values())
      .filter(schema => schema.inheritFrom === typeName);
    
    if (dependentSchemas.length > 0) {
      throw new Error(`Cannot unregister schema ${typeName}: it has dependent schemas`);
    }

    this.schemas.delete(typeName);
    this.inheritanceGraph.delete(typeName);
    
    this.emit('schema:unregistered', { typeName });
  }

  getSchema<T>(typeName: string): TypedElementSchema<T> | null {
    return this.schemas.get(typeName) as TypedElementSchema<T> || null;
  }

  getAllSchemas(): Map<string, TypedElementSchema> {
    return new Map(this.schemas);
  }

  // ===== Создание элементов =====

  async createElement<T>(typeName: string, data?: Partial<T>): Promise<ITypedElement<T>> {
    const schema = this.getSchema<T>(typeName);
    if (!schema) {
      throw new Error(`Schema ${typeName} not found`);
    }

    return this.createFromSchema(schema, data);
  }

  async createFromSchema<T>(schema: TypedElementSchema<T>, data?: Partial<T>): Promise<ITypedElement<T>> {
    if (schema.abstract) {
      throw new Error(`Cannot instantiate abstract type ${schema.name}`);
    }

    const element = new TypedElement<T>(schema.name, schema, undefined, this);
    
    if (data) {
      await element.setTypedData(data);
    }

    // Автоматическая валидация
    if (this.config.autoValidation) {
      const validation = await element.validateType();
      if (this.config.strictMode && !validation.isValid) {
        throw new Error(`Element validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
      }
    }

    this.elements.set(element.id, element as ITypedElement);
    this.emit('element:created', { element });
    
    return element;
  }

  // ===== Валидация схем =====

  validateSchema<T>(schema: TypedElementSchema<T>): SchemaValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const conflictingFields: string[] = [];
    const missingDependencies: string[] = [];

    // Проверка базовых полей
    if (!schema.name || schema.name.trim() === '') {
      errors.push('Schema name is required');
    }

    if (!schema.version || schema.version.trim() === '') {
      errors.push('Schema version is required');
    }

    if (!schema.fields || schema.fields.length === 0) {
      warnings.push('Schema has no fields defined');
    }

    // Проверка полей
    const fieldNames = new Set<string>();
    for (const field of schema.fields || []) {
      // Проверка уникальности имен полей
      const fieldName = String(field.name);
      if (fieldNames.has(fieldName)) {
        conflictingFields.push(fieldName);
        errors.push(`Duplicate field name: ${fieldName}`);
      }
      fieldNames.add(fieldName);

      // Проверка типа поля
      if (!this.isValidFieldType(field.type)) {
        errors.push(`Invalid field type: ${field.type} for field ${fieldName}`);
      }

      // Проверка валидации поля
      if (field.validation) {
        const validation = field.validation;
        
        if (validation.min !== undefined && validation.max !== undefined && validation.min > validation.max) {
          errors.push(`Invalid range for field ${fieldName}: min > max`);
        }

        if (validation.pattern) {
          try {
            new RegExp(validation.pattern);
          } catch (e) {
            errors.push(`Invalid regex pattern for field ${fieldName}: ${validation.pattern}`);
          }
        }
      }
    }

    // Проверка наследования
    if (schema.inheritFrom) {
      if (!this.schemas.has(schema.inheritFrom)) {
        missingDependencies.push(schema.inheritFrom);
        errors.push(`Parent schema not found: ${schema.inheritFrom}`);
      } else {
        const parentSchema = this.schemas.get(schema.inheritFrom);
        if (parentSchema?.sealed) {
          errors.push(`Cannot inherit from sealed type: ${schema.inheritFrom}`);
        }
      }
    }

    // Проверка глубины наследования
    if (schema.inheritFrom && this.config.inheritanceEnabled) {
      const depth = this.getInheritanceDepth(schema.name);
      if (depth > this.config.maxInheritanceDepth) {
        errors.push(`Inheritance depth exceeds maximum: ${depth} > ${this.config.maxInheritanceDepth}`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      conflictingFields,
      missingDependencies
    };
  }

  validateInheritance(typeName: string, parentTypeName: string): boolean {
    // Проверка на циклическое наследование
    const visited = new Set<string>();
    let current = parentTypeName;
    
    while (current) {
      if (visited.has(current)) {
        return false; // Циклическое наследование
      }
      
      if (current === typeName) {
        return false; // Попытка наследования от самого себя
      }
      
      visited.add(current);
      const parentSchema = this.schemas.get(current);
      current = parentSchema?.inheritFrom || '';
    }

    return true;
  }

  // ===== Поиск и фильтрация =====

  findElementsByType<T>(typeName: string): ITypedElement<T>[] {
    return Array.from(this.elements.values())
      .filter(element => element.typeName === typeName) as ITypedElement<T>[];
  }

  findElementsByField<T>(fieldName: keyof T, value: T[keyof T]): ITypedElement<T>[] {
    return Array.from(this.elements.values())
      .filter(element => {
        const fieldValue = element.getField(fieldName);
        return fieldValue === value;
      }) as ITypedElement<T>[];
  }

  // ===== Наследование =====

  buildInheritanceChain(typeName: string): string[] {
    const chain: string[] = [];
    const visited = new Set<string>();
    
    let current = typeName;
    while (current && !visited.has(current)) {
      visited.add(current);
      chain.push(current);
      
      const schema = this.schemas.get(current);
      if (!schema || !schema.inheritFrom) {
        break;
      }
      
      current = schema.inheritFrom;
    }
    
    return chain;
  }

  canInherit(childType: string, parentType: string): boolean {
    // Проверка на циклические зависимости
    const chain = this.buildInheritanceChain(childType);
    if (chain.includes(parentType) && chain.indexOf(parentType) > 0) {
      return false; // Циклическая зависимость
    }

    // Проверка на sealed типы
    const parentSchema = this.schemas.get(parentType);
    if (parentSchema?.sealed) {
      return false;
    }

    // Проверка глубины наследования
    const depth = this.getInheritanceDepth(childType);
    if (depth >= this.config.maxInheritanceDepth) {
      return false;
    }

    return true;
  }

  /**
   * Получает все поля с учетом наследования
   */
  getInheritedFields<T>(typeName: string): TypedFieldSchema<T>[] {
    const allFields = new Map<string, TypedFieldSchema<T>>();
    const chain = this.buildInheritanceChain(typeName);
    
    // Проходим по цепочке наследования от родителя к потомку
    for (let i = chain.length - 1; i >= 0; i--) {
      const currentType = chain[i];
      const schema = this.schemas.get(currentType);
      
      if (schema && schema.fields) {
        for (const field of schema.fields) {
          const fieldName = String(field.name);
          
          // Поле может быть переопределено в дочернем классе
          if (!allFields.has(fieldName) || i === 0) {
            allFields.set(fieldName, field as TypedFieldSchema<T>);
          }
        }
      }
    }
    
    return Array.from(allFields.values());
  }

  /**
   * Создает композитную схему из нескольких схем
   */
  createCompositeSchema<T>(
    name: string,
    version: string,
    sourceSchemas: string[],
    additionalFields?: TypedFieldSchema<T>[],
    options?: {
      description?: string;
      sealed?: boolean;
      abstract?: boolean;
      resolveConflicts?: 'first' | 'last' | 'merge' | 'error';
    }
  ): TypedElementSchema<T> {
    const resolveConflicts = options?.resolveConflicts || 'error';
    const allFields = new Map<string, TypedFieldSchema<T>>();
    const conflictingFields = new Set<string>();

    // Собираем поля из всех исходных схем
    for (const schemaName of sourceSchemas) {
      const schema = this.schemas.get(schemaName);
      if (!schema) {
        throw new Error(`Source schema not found: ${schemaName}`);
      }

      const inheritedFields = this.getInheritedFields<T>(schemaName);
      for (const field of inheritedFields) {
        const fieldName = String(field.name);
        
        if (allFields.has(fieldName)) {
          conflictingFields.add(fieldName);
          
          switch (resolveConflicts) {
            case 'first':
              // Оставляем первое поле
              break;
            case 'last':
              // Заменяем на последнее поле
              allFields.set(fieldName, field);
              break;
            case 'merge':
              // Пытаемся объединить поля
              const existingField = allFields.get(fieldName)!;
              const mergedField = this.mergeFields(existingField, field);
              allFields.set(fieldName, mergedField);
              break;
            case 'error':
              throw new Error(`Field name conflict: ${fieldName} in composite schema ${name}`);
          }
        } else {
          allFields.set(fieldName, field);
        }
      }
    }

    // Добавляем дополнительные поля
    if (additionalFields) {
      for (const field of additionalFields) {
        const fieldName = String(field.name);
        if (allFields.has(fieldName) && resolveConflicts === 'error') {
          throw new Error(`Field name conflict: ${fieldName} in composite schema ${name}`);
        }
        allFields.set(fieldName, field);
      }
    }

    const compositeSchema: TypedElementSchema<T> = {
      name,
      version,
      description: options?.description || `Composite schema from: ${sourceSchemas.join(', ')}`,
      fields: Array.from(allFields.values()),
      sealed: options?.sealed || false,
      abstract: options?.abstract || false,
      metadata: {
        isComposite: true,
        sourceSchemas: sourceSchemas,
        conflictingFields: Array.from(conflictingFields),
        resolveConflicts
      }
    };

    return compositeSchema;
  }

  /**
   * Объединяет два поля при композиции
   */
  private mergeFields<T>(field1: TypedFieldSchema<T>, field2: TypedFieldSchema<T>): TypedFieldSchema<T> {
    return {
      name: field1.name,
      type: field2.type || field1.type, // Приоритет у второго поля
      required: field2.required !== undefined ? field2.required : field1.required,
      defaultValue: field2.defaultValue !== undefined ? field2.defaultValue : field1.defaultValue,
      description: field2.description || field1.description,
      validation: {
        ...field1.validation,
        ...field2.validation
      },
      metadata: {
        ...field1.metadata,
        ...field2.metadata,
        mergedFrom: [field1.name, field2.name]
      }
    };
  }

  /**
   * Создает схему-наследник от существующей схемы
   */
  createInheritedSchema<T>(
    name: string,
    version: string,
    parentTypeName: string,
    additionalFields?: TypedFieldSchema<T>[],
    overrideFields?: Partial<Record<keyof T, TypedFieldSchema<T>>>,
    options?: {
      description?: string;
      sealed?: boolean;
      abstract?: boolean;
    }
  ): TypedElementSchema<T> {
    const parentSchema = this.schemas.get(parentTypeName);
    if (!parentSchema) {
      throw new Error(`Parent schema not found: ${parentTypeName}`);
    }

    if (parentSchema.sealed) {
      throw new Error(`Cannot inherit from sealed type: ${parentTypeName}`);
    }

    if (!this.canInherit(name, parentTypeName)) {
      throw new Error(`Cannot inherit: ${name} from ${parentTypeName}`);
    }

    // Получаем все поля родителя
    const parentFields = this.getInheritedFields<T>(parentTypeName);
    const fieldsMap = new Map<string, TypedFieldSchema<T>>();

    // Добавляем поля родителя
    for (const field of parentFields) {
      fieldsMap.set(String(field.name), field);
    }

    // Переопределяем поля, если указано
    if (overrideFields) {
      for (const [fieldName, fieldSchema] of Object.entries(overrideFields)) {
        if (fieldSchema) {
          fieldsMap.set(fieldName, fieldSchema as TypedFieldSchema<T>);
        }
      }
    }

    // Добавляем новые поля
    if (additionalFields) {
      for (const field of additionalFields) {
        const fieldName = String(field.name);
        if (fieldsMap.has(fieldName)) {
          throw new Error(`Field ${fieldName} already exists in parent schema`);
        }
        fieldsMap.set(fieldName, field);
      }
    }

    const inheritedSchema: TypedElementSchema<T> = {
      name,
      version,
      description: options?.description || `Inherited from ${parentTypeName}`,
      fields: Array.from(fieldsMap.values()),
      inheritFrom: parentTypeName,
      sealed: options?.sealed || false,
      abstract: options?.abstract || false,
      metadata: {
        isInherited: true,
        parentType: parentTypeName,
        inheritanceDepth: this.getInheritanceDepth(parentTypeName) + 1
      }
    };

    return inheritedSchema;
  }

  /**
   * Проверяет совместимость типов для присваивания
   */
  isTypeCompatible(childType: string, parentType: string): boolean {
    if (childType === parentType) {
      return true;
    }

    const chain = this.buildInheritanceChain(childType);
    return chain.includes(parentType);
  }

  /**
   * Получает общий родительский тип для двух типов
   */
  getCommonParentType(type1: string, type2: string): string | null {
    const chain1 = this.buildInheritanceChain(type1);
    const chain2 = this.buildInheritanceChain(type2);

    // Ищем первый общий тип
    for (const type of chain1) {
      if (chain2.includes(type)) {
        return type;
      }
    }

    return null;
  }

  /**
   * Создает интерфейс (абстрактный тип) с методами
   */
  createInterface<T>(
    name: string,
    version: string,
    fields: TypedFieldSchema<T>[],
    methods?: Record<string, Function>,
    options?: {
      description?: string;
      sealed?: boolean;
    }
  ): TypedElementSchema<T> {
    const interfaceSchema: TypedElementSchema<T> = {
      name,
      version,
      description: options?.description || `Interface ${name}`,
      fields,
      abstract: true,
      sealed: options?.sealed || false,
      metadata: {
        isInterface: true,
        methods: methods || {}
      }
    };

    return interfaceSchema;
  }

  /**
   * Реализует интерфейс в конкретном типе
   */
  implementInterface<T>(
    typeName: string,
    interfaceName: string,
    implementation?: Record<string, Function>
  ): void {
    const schema = this.schemas.get(typeName);
    const interfaceSchema = this.schemas.get(interfaceName);

    if (!schema) {
      throw new Error(`Type not found: ${typeName}`);
    }

    if (!interfaceSchema) {
      throw new Error(`Interface not found: ${interfaceName}`);
    }

    if (!interfaceSchema.abstract) {
      throw new Error(`${interfaceName} is not an interface`);
    }

    // Проверяем, что все поля интерфейса присутствуют в типе
    const typeFields = this.getInheritedFields<T>(typeName);
    const interfaceFields = interfaceSchema.fields;

    for (const interfaceField of interfaceFields) {
      const fieldName = String(interfaceField.name);
      const typeField = typeFields.find(f => String(f.name) === fieldName);
      
      if (!typeField) {
        throw new Error(`Type ${typeName} must implement field ${fieldName} from interface ${interfaceName}`);
      }

      // Проверяем совместимость типов полей
      if (typeField.type !== interfaceField.type) {
        throw new Error(`Field ${fieldName} type mismatch: expected ${interfaceField.type}, got ${typeField.type}`);
      }
    }

    // Добавляем информацию о реализации интерфейса
    schema.metadata = {
      ...schema.metadata,
      implementedInterfaces: [
        ...(schema.metadata?.implementedInterfaces || []),
        interfaceName
      ],
      interfaceImplementations: {
        ...schema.metadata?.interfaceImplementations,
        [interfaceName]: implementation || {}
      }
    };

    this.schemas.set(typeName, schema);
  }

  // ===== Статистика =====

  getTypeStats(): TypeStats {
    const typeDistribution = new Map<string, number>();
    const inheritanceDepth = new Map<string, number>();
    let validationErrors = 0;

    // Подсчет элементов по типам
    for (const element of this.elements.values()) {
      const typeName = element.typeName;
      typeDistribution.set(typeName, (typeDistribution.get(typeName) || 0) + 1);
      
      // Подсчет ошибок валидации
      const typedElement = element as TypedElement;
      const lastValidation = typedElement.getLastValidation?.();
      if (lastValidation && !lastValidation.isValid) {
        validationErrors++;
      }
    }

    // Подсчет глубины наследования
    for (const typeName of this.schemas.keys()) {
      inheritanceDepth.set(typeName, this.getInheritanceDepth(typeName));
    }

    // Самые используемые типы
    const mostUsedTypes = Array.from(typeDistribution.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([typeName, count]) => ({ typeName, count }));

    return {
      totalTypes: this.schemas.size,
      totalElements: this.elements.size,
      typeDistribution,
      inheritanceDepth,
      validationErrors,
      mostUsedTypes
    };
  }

  // ===== Утилиты =====

  private updateInheritanceGraph(schema: TypedElementSchema): void {
    if (schema.inheritFrom) {
      const parents = this.inheritanceGraph.get(schema.name) || [];
      parents.push(schema.inheritFrom);
      this.inheritanceGraph.set(schema.name, parents);
    }
  }

  private getInheritanceDepth(typeName: string): number {
    let depth = 0;
    let current = typeName;
    
    while (current) {
      const schema = this.schemas.get(current);
      if (schema?.inheritFrom) {
        depth++;
        current = schema.inheritFrom;
      } else {
        break;
      }
    }
    
    return depth;
  }

  private isValidFieldType(type: FieldType): boolean {
    const validTypes: FieldType[] = [
      'string', 'number', 'boolean', 'date', 'array', 'object',
      'reference', 'computed', 'enum', 'union', 'generic'
    ];
    return validTypes.includes(type);
  }

  // ===== Управление элементами =====

  removeElement(elementId: string): boolean {
    return this.elements.delete(elementId);
  }

  getElement(elementId: string): ITypedElement | null {
    return this.elements.get(elementId) || null;
  }

  getAllElements(): ITypedElement[] {
    return Array.from(this.elements.values());
  }

  // ===== Конфигурация =====

  updateConfig(config: Partial<TypedElementConfig>): void {
    this.config = { ...this.config, ...config };
    this.emit('config:updated', { config: this.config });
  }

  getConfig(): TypedElementConfig {
    return { ...this.config };
  }

  // ===== Очистка =====

  clear(): void {
    this.schemas.clear();
    this.elements.clear();
    this.inheritanceGraph.clear();
    this.emit('manager:cleared');
  }

  // ===== Экспорт/Импорт =====

  exportSchemas(): Record<string, TypedElementSchema> {
    return Object.fromEntries(this.schemas);
  }

  importSchemas(schemas: Record<string, TypedElementSchema>): void {
    for (const [, schema] of Object.entries(schemas)) {
      this.registerSchema(schema);
    }
  }
}

// Глобальный экземпляр менеджера
export const defaultTypedElementManager = new TypedElementManager(); 